import { readGiftCardCatalog, readGiftCardProduct } from "@/lib/gift-card-public.functions";

/**
 * NexKey API client.
 *
 * Talks to the self-hosted Express backend defined in /backend.
 * Configure with VITE_API_BASE_URL (default: http://localhost:4000).
 *
 * NO mock data: every call hits the real API. When the API is unreachable
 * or empty, components show professional empty / loading / error states.
 */

function resolveApiBaseUrl(): string {
  const envUrl =
    typeof import.meta !== "undefined" && (import.meta as any).env?.VITE_API_BASE_URL;
  if (envUrl) return String(envUrl).replace(/\/$/, "");

  // In the browser: only use localhost when actually running on localhost.
  // Otherwise (production, previews, custom domains) call the same-origin /api.
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1" || host === "0.0.0.0") {
      return "http://localhost:4000";
    }
    return "";
  }

  // SSR fallback (no window). Prefer same-origin in prod, localhost in dev.
  if (typeof process !== "undefined" && process.env?.NODE_ENV === "production") {
    return "";
  }
  return "http://localhost:4000";
}

export const API_BASE_URL = resolveApiBaseUrl();

export interface ApiEnvelope<T> { data: T }
export interface Paged<T> { data: T[]; page: number; limit: number; total: number }

export interface Movie {
  id: string;
  steamMovieId?: number | null;
  name: string;
  thumbnail?: string | null;
  webm480?: string | null;
  webmMax?: string | null;
  mp4_480?: string | null;
  mp4Max?: string | null;
  highlight?: boolean;
  sortOrder?: number;
}

export interface Game {
  id: string;
  slug: string;
  title: string;
  shortDescription?: string | null;
  fullDescription?: string | null;
  headerImage?: string | null;
  capsuleImage?: string | null;
  backgroundImage?: string | null;
  releaseDate?: string | null;
  releaseDateRaw?: string | null;
  comingSoon?: boolean;
  developer?: string | null;
  publisher?: string | null;
  developers?: string[];
  publishers?: string[];
  website?: string | null;
  steamUrl?: string | null;
  steamAppid?: number | null;
  steamPriceInitial?: number | null;
  steamPriceFinal?: number | null;
  steamDiscountPercent?: number | null;
  steamCurrency?: string;
  metacriticScore?: number | null;
  metacriticUrl?: string | null;
  genres?: { genre: { id: string; name: string; slug: string } }[];
  categories?: { category: { id: string; name: string; slug: string } }[];
  screenshots?: { id: string; url: string }[];
  movies?: Movie[];
  bestOffer?: number | null;
  /** Coupon code applied to `bestOffer` (if the cheapest shop has one). */
  bestOfferCouponCode?: string | null;
  savingsPercent?: number | null;
  storeCount?: number;
  isFree?: boolean;



  // Extended Steam appdetails fields (persisted by the importer).
  pcRequirements?: { minimum?: string | null; recommended?: string | null } | null;
  macRequirements?: { minimum?: string | null; recommended?: string | null } | null;
  linuxRequirements?: { minimum?: string | null; recommended?: string | null } | null;
  supportedLanguages?: string | null; // Steam raw HTML string
  languages?: { name: string; interface?: boolean; fullAudio?: boolean; subtitles?: boolean }[];
  platforms?: { windows?: boolean; mac?: boolean; linux?: boolean } | null;
  controllerSupport?: string | null;
  achievementsTotal?: number | null;
  achievements?: { total?: number; highlighted?: { name: string; path: string }[] } | null;
  legalNotice?: string | null;
  packages?: { packages?: number[]; package_groups?: unknown[] } | null;
  dlc?: number[] | null;
  /** "game" | "dlc". */
  type?: string;
  parentGameId?: string | null;
  /** Base game of a DLC (DLC pages only). */
  parentGame?: Pick<Game, "id" | "slug" | "title" | "headerImage" | "capsuleImage"> | null;
  /** Visible, purchasable DLC products of a base game. */
  dlcs?: Array<
    Pick<Game, "id" | "slug" | "title" | "headerImage" | "capsuleImage" | "steamPriceFinal" | "steamCurrency" | "releaseDate" | "bestOffer" | "bestOfferCouponCode" | "savingsPercent" | "storeCount">
  >;
  dlcCount?: number;
}


export type PriceHistoryRange = "7d" | "30d" | "90d" | "all";

export interface PriceHistoryPoint {
  recordedAt: string;
  /** Integer cents. */
  price: number;
}

export interface PriceHistory {
  range: PriceHistoryRange;
  currency: string;
  currentBest: number | null;
  historicalLow: number | null;
  historySince: string | null;
  trackedShops: number;
  points: PriceHistoryPoint[];
}

export interface Shop {
  id: string;
  name: string;
  slug: string;
  logoUrl?: string | null;
  websiteUrl?: string | null;
  trustScore?: string | number | null;
  isActive?: boolean;
}

export interface GiftCard {
  id: string;
  name: string;
  normalizedName: string;
  brand: string;
  productType: string;
  category: string;
  /** Face value in minor units, independent of shop sale prices. */
  value: number;
  currency: string;
  region: string;
  image?: string | null;
  description?: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: { offers: number };
}
export type GiftCardInput = Pick<GiftCard, "name" | "brand" | "productType" | "category" | "value" | "currency" | "region" | "image" | "description" | "active">;

/** Gift-card listings never pretend to be game offers. */
export type GiftCardOffer = Omit<Offer, "gameId" | "game"> & { giftCardId: string };
export type GiftCardDetail = GiftCard & { offers: GiftCardOffer[] };

export interface Offer {
  id: string;
  gameId: string;
  shopId: string;
  /** Shop-side listing id; identifies one external listing across currencies. */
  externalId?: string | null;
  title?: string | null;

  edition?: string | null;
  platform?: string | null;
  drmType?: string | null;
  region?: string | null;
  price: number;
  currency: string;
  originalPrice?: number | null;
  stockStatus: string;
  affiliateUrl: string;
  directUrl?: string | null;
  lastChecked?: string | null;
  isActive: boolean;
  updatedAt: string;
  shop: Shop;
  game?: { id: string; slug: string; title: string; headerImage?: string | null; steamPriceFinal?: number | null; steamCurrency?: string };
  savingsPercent?: number | null;
  /** Coupon-adjusted price in cents (equals `price` when no coupon applies). */
  effectivePrice?: number | null;
  /** Coupon code the user must enter at checkout to unlock `effectivePrice`. */
  couponCode?: string | null;
  /** Percentage off applied by the coupon. */
  couponPercent?: number | null;
}


export interface AccountUser {
  id: string;
  email: string;
  role: string;
  createdAt?: string;
}

export interface PriceAlert {
  id: string;
  gameId: string;
  /** Target price in cents of `currency`. */
  targetPrice: number;
  currency: string;
  active: boolean;
  lastNotifiedAt?: string | null;
  lastNotifiedPrice?: number | null;
  createdAt: string;
  updatedAt: string;
  /** Live best price in cents (cheapest store, else Steam). */
  currentPrice: number | null;
  currentCurrency: string;
  /** currentPrice - targetPrice; negative means already below target. */
  difference: number | null;
  triggered: boolean;
  game: Pick<Game, "id" | "slug" | "title" | "headerImage" | "capsuleImage" | "steamPriceFinal" | "steamCurrency" | "bestOffer" | "savingsPercent" | "bestOfferCouponCode" | "storeCount">;
}

export interface Genre { id: string; name: string; slug: string; count?: number }
export interface SiteStats { games: number; shops: number; offers: number; genres: number }


export interface ImportJob {
  id: string;
  source: string;
  status: string;
  startedAt: string;
  finishedAt?: string | null;
  totalItems: number;
  importedItems: number;
  failedItems: number;
  matchedItems?: number;
  unmatchedItems?: number;
  errorMessage?: string | null;
}

export interface Overview {
  games: number;
  shops: number;
  offers: number;
  pendingGames: number;
  lastJob: ImportJob | null;
}

class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

function getToken(): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem("nexkey_token");
}

export function setToken(token: string | null) {
  if (typeof window === "undefined") return;
  if (token) window.localStorage.setItem("nexkey_token", token);
  else window.localStorage.removeItem("nexkey_token");
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  const token = getToken();
  if (token && !headers.has("Authorization")) headers.set("Authorization", `Bearer ${token}`);
  const res = await fetch(`${API_BASE_URL}${path}`, { ...init, headers });
  if (!res.ok) {
    let msg = `Request failed (${res.status})`;
    try {
      const j = (await res.json()) as { error?: string };
      if (j?.error) msg = j.error;
    } catch { /* noop */ }
    throw new ApiError(res.status, msg);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  // public
  giftCards: (brand?: string, page = 1) => readGiftCardCatalog({ data: { brand, page } }),
  giftCard: (id: string) => readGiftCardProduct({ data: { id } }),
  games: (params: { page?: number; limit?: number; q?: string; genre?: string; sort?: string; minPrice?: number; maxPrice?: number; free?: boolean; upcoming?: boolean; released?: boolean; recentDays?: number; discounted?: boolean; minDiscount?: number; year?: number; controller?: boolean; platforms?: string } = {}) => {
    const s = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => v != null && v !== "" && v !== false && s.set(k, String(v)));
    return request<Paged<Game>>(`/api/games?${s.toString()}`);
  },
  game: (slug: string) => request<ApiEnvelope<Game>>(`/api/games/${slug}`),
  gameOffers: (slug: string) => request<ApiEnvelope<Offer[]>>(`/api/games/${slug}/offers`),
  gamePriceHistory: (slug: string, range: PriceHistoryRange = "30d") =>
    request<ApiEnvelope<PriceHistory>>(`/api/games/${slug}/price-history?range=${range}`),
  trending: () => request<ApiEnvelope<Game[]>>("/api/games/trending"),
  newReleases: () => request<ApiEnvelope<Game[]>>("/api/games/new-releases"),
  bestDeals: () => request<ApiEnvelope<Offer[]>>("/api/deals/best"),
  latestDeals: () => request<ApiEnvelope<Offer[]>>("/api/deals/latest"),
  search: (q: string) => request<ApiEnvelope<Game[]>>(`/api/search?q=${encodeURIComponent(q)}`),
  genres: () => request<ApiEnvelope<Genre[]>>("/api/genres"),
  shops: () => request<ApiEnvelope<Shop[]>>("/api/shops"),
  stats: () => request<ApiEnvelope<SiteStats>>("/api/stats/overview"),


  // auth
  login: (email: string, password: string) =>
    request<{ token: string; user: { id: string; email: string; role: string } }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),

  register: (email: string, password: string) =>
    request<{ token: string; user: { id: string; email: string; role: string } }>("/api/auth/register", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),
  me: () => request<ApiEnvelope<AccountUser>>("/api/auth/me"),

  // favorites (authenticated)
  favorites: () => request<ApiEnvelope<Game[]>>("/api/favorites"),
  addFavorite: (gameId: string) => request<ApiEnvelope<{ gameId: string }>>(`/api/favorites/${gameId}`, { method: "POST" }),
  removeFavorite: (gameId: string) => request<void>(`/api/favorites/${gameId}`, { method: "DELETE" }),

  gamesByIds: (ids: string[]) =>
    ids.length
      ? request<ApiEnvelope<Game[]>>(`/api/games/by-ids?ids=${encodeURIComponent(ids.join(","))}`)
      : Promise.resolve({ data: [] as Game[] }),

  // price alerts (authenticated)
  priceAlerts: (opts: { all?: boolean } = {}) =>
    request<ApiEnvelope<PriceAlert[]>>(`/api/price-alerts${opts.all ? "?all=1" : ""}`),
  createPriceAlert: (body: { gameId: string; targetPrice: number; currency: string }) =>
    request<ApiEnvelope<PriceAlert>>("/api/price-alerts", { method: "POST", body: JSON.stringify(body) }),
  updatePriceAlert: (id: string, body: { targetPrice?: number; currency?: string; active?: boolean }) =>
    request<ApiEnvelope<PriceAlert>>(`/api/price-alerts/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  deletePriceAlert: (id: string) => request<void>(`/api/price-alerts/${id}`, { method: "DELETE" }),


  // admin
  adminGiftCards: (params: { q?: string; brand?: string; region?: string; category?: string; value?: number; page?: number } = {}) => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => value != null && value !== "" && search.set(key, String(value)));
    return request<Paged<GiftCard>>(`/api/admin/gift-cards?${search}`);
  },
  adminCreateGiftCard: (body: GiftCardInput) => request<ApiEnvelope<GiftCard>>("/api/admin/gift-cards", { method: "POST", body: JSON.stringify(body) }),
  adminUpdateGiftCard: (id: string, body: Partial<GiftCardInput>) => request<ApiEnvelope<GiftCard>>(`/api/admin/gift-cards/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  adminOverview: () => request<ApiEnvelope<Overview>>("/api/admin/overview"),
  adminShops: () => request<ApiEnvelope<Shop[]>>("/api/admin/shops"),
  adminCreateShop: (body: Partial<Shop>) => request<ApiEnvelope<Shop>>("/api/admin/shops", { method: "POST", body: JSON.stringify(body) }),
  adminUpdateShop: (id: string, body: Partial<Shop>) => request<ApiEnvelope<Shop>>(`/api/admin/shops/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  adminDeleteShop: (id: string) => request<void>(`/api/admin/shops/${id}`, { method: "DELETE" }),

  adminOffers: (gameId?: string) => request<ApiEnvelope<Offer[]>>(`/api/admin/offers${gameId ? `?gameId=${gameId}` : ""}`),
  adminCreateOffer: (body: Partial<Offer> & { gameId: string; shopId: string; price: number; affiliateUrl: string }) =>
    request<ApiEnvelope<Offer>>("/api/admin/offers", { method: "POST", body: JSON.stringify(body) }),
  adminUpdateOffer: (id: string, body: Partial<Offer>) =>
    request<ApiEnvelope<Offer>>(`/api/admin/offers/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  adminDeleteOffer: (id: string) => request<void>(`/api/admin/offers/${id}`, { method: "DELETE" }),

  adminCreateGame: (body: any) => request<ApiEnvelope<Game>>("/api/admin/games", { method: "POST", body: JSON.stringify(body) }),
  adminUpdateGame: (id: string, body: any) => request<ApiEnvelope<Game>>(`/api/admin/games/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  adminDeleteGame: (id: string) => request<void>(`/api/admin/games/${id}`, { method: "DELETE" }),

  adminImportAppList: () => request<ApiEnvelope<ImportJob>>("/api/admin/import/steam/app-list", { method: "POST" }),
  adminImportBulk: (limit: number) => request<ApiEnvelope<ImportJob>>("/api/admin/import/steam/bulk", { method: "POST", body: JSON.stringify({ limit }) }),
  adminImportApp: (appid: number) => request<ApiEnvelope<Game>>(`/api/admin/import/steam/app/${appid}`, { method: "POST" }),
  adminImportJobs: () => request<ApiEnvelope<ImportJob[]>>("/api/admin/import/jobs"),
  adminImportErrors: (jobId?: string) => request<ApiEnvelope<any[]>>(`/api/admin/import/errors${jobId ? `?jobId=${jobId}` : ""}`),
  adminImportIncomplete: (limit: number) => request<ApiEnvelope<ImportJob>>("/api/admin/import/steam/incomplete", { method: "POST", body: JSON.stringify({ limit }) }),
  adminImportIncompleteCount: () => request<ApiEnvelope<{ count: number }>>("/api/admin/import/steam/incomplete/count"),

  // GameBillet
  adminGameBilletStatus: () => request<ApiEnvelope<{
    currencies: { currency: "EUR" | "GBP" | "USD"; configured: boolean; feedUrl: string | null }[];
    jobs: ImportJob[];
    shop: Shop | null;
    offerCount: number;
  }>>("/api/admin/import/gamebillet/status"),
  adminImportGameBillet: (currency?: "EUR" | "GBP" | "USD") =>
    request<ApiEnvelope<ImportJob[]>>("/api/admin/import/gamebillet/run", {
      method: "POST",
      body: JSON.stringify(currency ? { currency } : {}),
    }),

  // GAMIVO
  adminGamivoStatus: () => request<ApiEnvelope<{
    feedUrl: string;
    affiliateTagConfigured: boolean;
    jobs: ImportJob[];
    shop: Shop | null;
    offerCount: number;
  }>>("/api/admin/import/gamivo/status"),
  adminImportGamivo: (opts: { limit?: number; dryRun?: boolean; mode?: "game" | "gift_card" } = {}) =>
    request<ApiEnvelope<ImportJob>>("/api/admin/import/gamivo/run", {
      method: "POST",
      body: JSON.stringify(opts),
    }),

  // ENEBA
  adminEnebaStatus: () => request<ApiEnvelope<{
    feedUrl: string;
    affiliateTagConfigured: boolean;
    currency: string;
    locale: string;
    jobs: ImportJob[];
    shop: Shop | null;
    offerCount: number;
  }>>("/api/admin/import/eneba/status"),
  adminImportEneba: (opts: { limit?: number; dryRun?: boolean; mode?: "game" | "gift_card" } = {}) =>
    request<ApiEnvelope<ImportJob>>("/api/admin/import/eneba/run", {
      method: "POST",
      body: JSON.stringify(opts),
    }),

  // Gamesplanet
  adminGamesplanetStatus: () => request<ApiEnvelope<{
    regions: { region: "DE" | "FR" | "UK" | "US"; currency: string; feedUrl: string }[];
    affiliateTag: string;
    jobs: ImportJob[];
    shop: Shop | null;
    offerCount: number;
  }>>("/api/admin/import/gamesplanet/status"),
  adminImportGamesplanet: (opts: { limit?: number; dryRun?: boolean; region?: "DE" | "FR" | "UK" | "US" } = {}) =>
    request<ApiEnvelope<ImportJob>>("/api/admin/import/gamesplanet/run", {
      method: "POST",
      body: JSON.stringify(opts),
    }),

  // GameBoost
  adminGameBoostStatus: () => request<ApiEnvelope<{
    feedUrl: string;
    affiliateTag: string;
    currencies: ("EUR" | "USD")[];
    coupon: { code: string; percentOff: number } | null;
    jobs: ImportJob[];
    shop: Shop | null;
    offerCount: number;
  }>>("/api/admin/import/gameboost/status"),
  adminImportGameBoost: (opts: { limit?: number; dryRun?: boolean } = {}) =>
    request<ApiEnvelope<ImportJob>>("/api/admin/import/gameboost/run", {
      method: "POST",
      body: JSON.stringify(opts),
    }),

  // Gaming Dragons
  adminGamingDragonsStatus: () => request<ApiEnvelope<{
    currencies: { currency: "EUR" | "GBP" | "USD"; feedUrl: string }[];
    affiliate: { configured: boolean; tagKey: string | null };
    jobs: ImportJob[];
    shop: Shop | null;
    offerCount: number;
  }>>("/api/admin/import/gamingdragons/status"),
  adminImportGamingDragons: (opts: { limit?: number; dryRun?: boolean; currency?: "EUR" | "GBP" | "USD" } = {}) =>
    request<ApiEnvelope<ImportJob>>("/api/admin/import/gamingdragons/run", {
      method: "POST",
      body: JSON.stringify(opts),
    }),

  // GamersGate
  adminGamersGateStatus: () => request<ApiEnvelope<{
    feedUrl: string;
    affiliateConfigured: boolean;
    sourceRegion: string;
    currency: string;
    jobs: ImportJob[];
    shop: Shop | null;
    offerCount: number;
  }>>("/api/admin/import/gamersgate/status"),
  adminImportGamersGate: (opts: { limit?: number; dryRun?: boolean } = {}) =>
    request<ApiEnvelope<ImportJob>>("/api/admin/import/gamersgate/run", {
      method: "POST",
      body: JSON.stringify(opts),
    }),
  // Electronic First
  adminElectronicFirstStatus: () => request<ApiEnvelope<{
    feeds: { currency: string; url: string }[];
    jobs: ImportJob[];
    shop: Shop | null;
    offerCounts: { currency: string; count: number }[];
  }>>("/api/admin/import/electronicfirst/status"),
  adminImportElectronicFirst: (opts: { currency: "EUR" | "GBP" | "USD"; limit?: number; dryRun?: boolean }) =>
    request<ApiEnvelope<ImportJob>>("/api/admin/import/electronicfirst/run", {
      method: "POST",
      body: JSON.stringify(opts),
    }),
  // CJS CD Keys (AWIN)
  adminCjsStatus: () => request<ApiEnvelope<{
    feedUrl: string;
    network: string;
    awin: { advertiserId: string; publisherId: string };
    jobs: ImportJob[];
    shop: Shop | null;
    totalOffers: number;
    lastSuccess: ImportJob | null;
    recentErrors: { id: string; errorMessage: string; createdAt: string }[];
    offerCounts: { currency: string; count: number }[];
  }>>("/api/admin/import/cjscdkeys/status"),
  adminImportCjs: (opts: { limit?: number; dryRun?: boolean }) =>
    request<ApiEnvelope<ImportJob>>("/api/admin/import/cjscdkeys/run", {
      method: "POST",
      body: JSON.stringify(opts),
    }),
};

export { ApiError };
